import { PixelStreaming } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5'
import mixpanel from 'mixpanel-browser'

import { WebRTCMetrics } from './types'

export const startWebRTCStatsCollection = (
  pixelStreaming: PixelStreaming,
  intervalMs = 10000
): (() => void) => {
  const peerConnection =
    // @ts-expect-error - _webRtcController is needed for streaming stats
    pixelStreaming._webRtcController?.peerConnectionController?.peerConnection

  if (!peerConnection) {
    return () => {}
  }

  let previousStats = {
    framesDecoded: 0,
    bytesReceived: 0,
    framesDropped: 0,
    timestamp: 0,
  }

  const collectStats = async () => {
    try {
      const stats = await peerConnection.getStats()

      const currentTimestamp = Date.now()
      const timeDiff = (currentTimestamp - previousStats.timestamp) / 1000
      const webRTCMetrics: WebRTCMetrics = {
        timestamp: currentTimestamp,
      }
      stats.forEach((report) => {
        // Handle the Candidate Pair report
        if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          webRTCMetrics.round_trip_time_ms = report.currentRoundTripTime
          webRTCMetrics.available_outgoing_bitrate_kbps =
            report.availableOutgoingBitrate
              ? report.availableOutgoingBitrate
              : undefined
          // Handle the Inbound RTP report
        } else if (report.type === 'inbound-rtp' && report.kind === 'video') {
          webRTCMetrics.jitter_seconds = report.jitter
          webRTCMetrics.frames_per_second = report.framesPerSecond
          webRTCMetrics.frames_dropped = report.framesDropped

          // Calculate frames received/s and frames decoded/s
          if (timeDiff > 0 && previousStats.timestamp > 0) {
            const framesDecodedPerSec =
              (report.framesDecoded - previousStats.framesDecoded) / timeDiff
            const actualBitrate =
              ((report.bytesReceived - (previousStats.bytesReceived || 0)) *
                8) /
              timeDiff /
              1000
            const framesDroppedPerSec =
              (report.framesDropped - previousStats.framesDropped) / timeDiff
            webRTCMetrics.frames_dropped_per_second = framesDroppedPerSec
            webRTCMetrics.frames_decoded_per_second = framesDecodedPerSec
            webRTCMetrics.actual_bitrate_kbps = actualBitrate
          }

          previousStats = {
            framesDecoded: report.framesDecoded || 0,
            bytesReceived: report.bytesReceived || 0,
            framesDropped: report.framesDropped || 0,
            timestamp: currentTimestamp,
          }
        }
      })

      mixpanel.track('WebRTC Performance Stats', {
        ...webRTCMetrics,
        component: 'kestrel_pixel_streaming',
      })
    } catch (error) {
      mixpanel.track('WebRTC Stats Collection Error', {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        component: 'kestrel_pixel_streaming',
      })
    }
  }

  // Collect stats at specified interval
  const statsInterval = setInterval(collectStats, intervalMs)

  // Initial stats collection
  collectStats()

  // Return cleanup function
  return () => clearInterval(statsInterval)
}
